import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { setGlobalUserInfo } from '../utils/buryReport';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  // 🔧 从 localStorage 初始化 user 和 isAuthenticated
  const [user, setUser] = useState(() => {
    try {
      const savedUser = localStorage.getItem('user');
      return savedUser ? JSON.parse(savedUser) : null;
    } catch (error) {
      console.error('❌ Failed to parse user from localStorage:', error);
      return null;
    }
  });
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    return localStorage.getItem('isAuthenticated') === 'true';
  });
  const [loading, setLoading] = useState(true);

  // Set up axios defaults with credentials
  useEffect(() => {
    axios.defaults.withCredentials = true;
  }, []);

  // Check if user is already logged in
  useEffect(() => {
    const checkAuth = async () => {
      try {
        console.log('🔍 Client: Checking authentication...');
        // Use new status endpoint
        const response = await axios.get('/api/auth/status', {
          headers: {
            'Cache-Control': 'no-cache',
            'Pragma': 'no-cache'
          }
        });
        console.log('🔍 Client: Auth response:', response.data);
        if (response.data.authenticated && response.data.user) {
          setUser(response.data.user);
          localStorage.setItem('user', JSON.stringify(response.data.user));
          localStorage.setItem('userName', response.data.user.name);
          localStorage.setItem('isAuthenticated', 'true');
          setIsAuthenticated(true);
          // 设置全局用户信息用于埋点
          setGlobalUserInfo({
            userId: response.data.user.id,
            email: response.data.user.email
          });
          console.log('✅ Client: User authenticated successfully');
        } else {
          setIsAuthenticated(false);
          localStorage.removeItem('user');
          localStorage.removeItem('isAuthenticated');
        }
      } catch (error) {
        // User not authenticated, which is fine
        console.log('❌ Client: User not authenticated:', error.response?.status, error.response?.data);
        setIsAuthenticated(false);
        localStorage.removeItem('user');
        localStorage.removeItem('isAuthenticated');
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, []);

  const sendCode = async (email) => {
    try {
      console.log('📧 Client: Sending verification code...');
      const response = await axios.post('/api/auth/send-code', {
        email
      });

      console.log('📧 Client: Send code response:', response.data);
      if (response.data.success) {
        toast.success(`Verification code sent to ${email}`);
        return {
          success: true,
          expiresInMinutes: response.data.expiresInMinutes
        };
      } else {
        toast.error(response.data.error || 'Failed to send verification code');
        return { success: false, error: response.data.error };
      }
    } catch (error) {
      const message = error.response?.data?.error || 'Failed to send verification code';
      console.error('❌ Client: Send code error:', error);
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const verifyCode = async (email, code) => {
    try {
      console.log('🔐 Client: Verifying code...');
      const response = await axios.post('/api/auth/verify-code', {
        email,
        code
      });

      console.log('🔐 Client: Verify code response:', response.data);
      if (response.data.success) {
        const { user } = response.data;
        setUser(user);
        localStorage.setItem('user', JSON.stringify(user));
        localStorage.setItem('userName', user.name);
        localStorage.setItem('isAuthenticated', 'true');
        setIsAuthenticated(true);
        // 设置全局用户信息用于埋点
        setGlobalUserInfo({
          userId: user.id,
          email: user.email
        });
        console.log('✅ Client: Login successful, user set:', user);
        toast.success('Login successful!');
        return true;
      } else {
        toast.error(response.data.error || 'Invalid verification code');
        return false;
      }
    } catch (error) {
      const message = error.response?.data?.error || 'Verification failed';
      console.error('❌ Client: Verify code error:', error);
      toast.error(message);
      return false;
    }
  };

  /**
   * Username + password login (demo admin path). Used in parallel with the
   * primary email-verification flow — same session cookie, same downstream
   * gates, no special-casing needed. Backed by POST /api/auth/login on the
   * server (env: DEMO_ADMIN_USER / DEMO_ADMIN_PASS / DEMO_ADMIN_EMAIL).
   */
  const login = async (username, password) => {
    try {
      const response = await axios.post('/api/auth/login', { username, password });
      if (response.data?.success) {
        const { user: authUser } = response.data;
        setUser(authUser);
        localStorage.setItem('user', JSON.stringify(authUser));
        localStorage.setItem('userName', authUser.name);
        localStorage.setItem('isAuthenticated', 'true');
        setIsAuthenticated(true);
        setGlobalUserInfo({ userId: authUser.id, email: authUser.email });
        toast.success('Login successful!');
        return true;
      }
      toast.error(response.data?.error || 'Invalid username or password');
      return false;
    } catch (error) {
      const message = error.response?.data?.error || 'Login failed';
      console.error('❌ Client: Password login error:', error);
      toast.error(message);
      return false;
    }
  };

  const register = async (userData) => {
    try {
      console.log('👤 Client: Attempting registration...');
      const response = await axios.post('/api/auth/register', userData);

      console.log('👤 Client: Registration response:', response.data);
      if (response.data.success) {
        const { user } = response.data;
        setUser(user);
        setIsAuthenticated(true);
        // 设置全局用户信息用于埋点
        setGlobalUserInfo({
          userId: user.id,
          email: user.email
        });
        console.log('✅ Client: Registration successful, user set:', user);
        toast.success('Registration successful!');
        return { success: true, user };
      } else {
        toast.error(response.data.error || 'Registration failed');
        return { success: false, error: response.data.error };
      }
    } catch (error) {
      const message = error.response?.data?.error || 'Registration failed';
      console.error('❌ Client: Registration error:', error);
      toast.error(message);
      return { success: false, error: message };
    }
  };

  /**
   * Save onboarding profile (displayName, jobTitle, discipline) and merge the
   * returned fields back into the in-memory + localStorage user object so the
   * profile-complete gate flips immediately.
   */
  const saveProfile = async ({ displayName, jobTitle, discipline }) => {
    try {
      const response = await axios.post('/api/auth/profile', {
        displayName,
        jobTitle,
        discipline
      });
      if (response.data?.success) {
        const merged = { ...(user || {}), ...response.data.user };
        setUser(merged);
        localStorage.setItem('user', JSON.stringify(merged));
        return { success: true, user: merged };
      }
      const err = response.data?.error || 'Could not save profile';
      toast.error(err);
      return { success: false, error: err };
    } catch (error) {
      const message = error.response?.data?.error || 'Could not save profile';
      console.error('❌ Client: Save profile error:', error);
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const logout = async () => {
    try {
      await axios.post('/api/auth/logout');
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setUser(null);
      setIsAuthenticated(false);
      localStorage.removeItem('user');
      localStorage.removeItem('isAuthenticated');
      localStorage.removeItem('userName');
      // 清除全局用户信息
      setGlobalUserInfo(null);
      toast.success('Logged out successfully');
    }
  };

  const value = {
    user,
    isAuthenticated,
    loading,
    login,
    sendCode,
    verifyCode,
    register,
    saveProfile,
    logout
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 